// Appointments Module
// Handles appointment-related functionality

import { Modal } from 'bootstrap';

export class AppointmentManager {
    constructor() {
        this.ajaxUrl = window.appraisal_ajax?.ajax_url || '/wp-admin/admin-ajax.php';
        this.nonce = window.appraisal_ajax?.nonce || '';
    }

    // Update appointment status
    updateAppointmentStatus(appointmentId, status) {
        this.showConfirmation(
            `Are you sure you want to ${status} this appointment?`,
            () => this.performStatusUpdate(appointmentId, status)
        );
    }

    // Show custom confirmation modal
    showConfirmation(message, onConfirm) {
        // Remove any existing confirmation modal
        const existingModal = document.getElementById('confirmationModal');
        if (existingModal) {
            existingModal.remove();
        }

        // Create modal HTML
        const modalHtml = `
            <div class="modal fade" id="confirmationModal" tabindex="-1" aria-labelledby="confirmationModalLabel" aria-hidden="true">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="confirmationModalLabel">
                                <span class="fas fa-question-circle text-primary me-2"></span>
                                Confirm Action
                            </h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-0">${message}</p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                            <button type="button" class="btn btn-primary" id="confirmActionBtn">
                                <span class="fas fa-check me-2"></span>Confirm
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Add modal to page
        document.body.insertAdjacentHTML('beforeend', modalHtml);

        // Show modal
        const modal = new Modal(document.getElementById('confirmationModal'));
        modal.show();

        // Handle confirm button click
        document.getElementById('confirmActionBtn').addEventListener('click', () => {
            modal.hide();
            onConfirm();
        });

        // Clean up modal when hidden
        document.getElementById('confirmationModal').addEventListener('hidden.bs.modal', () => {
            document.getElementById('confirmationModal').remove();
        });
    }

        // Perform the actual status update
        performStatusUpdate(appointmentId, status) {
        const formData = new FormData();
        formData.append('action', 'update_appointment_status');
        formData.append('appointment_id', appointmentId);
        formData.append('status', status);
        formData.append('nonce', this.nonce);

        fetch(this.ajaxUrl, {
            method: 'POST',
            body: formData
        })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                this.showNotification('Appointment status updated successfully!', 'success');
                // Refresh the page or update the UI
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
                } else {
                    this.showNotification('Error: ' + data.data, 'danger');
                }
        })
        .catch(error => {
            console.error('Error:', error);
            this.showNotification('An error occurred. Please try again.', 'danger');
        });
    }

    // Delete appointment
    deleteAppointment(appointmentId) {
        this.showDeleteConfirmation(
            'Are you sure you want to permanently delete this appointment? This will completely remove it from the system for both the appraiser and appraisee, and cannot be undone.',
            () => this.performDelete(appointmentId)
        );
    }

    // Show custom delete confirmation modal
    showDeleteConfirmation(message, onConfirm) {
        // Remove any existing confirmation modal
        const existingModal = document.getElementById('deleteConfirmationModal');
        if (existingModal) {
            existingModal.remove();
        }

        // Create modal HTML
        const modalHtml = `
            <div class="modal fade" id="deleteConfirmationModal" tabindex="-1" aria-labelledby="deleteConfirmationModalLabel" aria-hidden="true">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="deleteConfirmationModalLabel">
                                <span class="fas fa-exclamation-triangle text-warning me-2"></span>
                                Confirm Deletion
                            </h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-0">${message}</p>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                            <button type="button" class="btn btn-danger" id="confirmDeleteBtn">
                                <span class="fas fa-trash me-2"></span>Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Add modal to page
        document.body.insertAdjacentHTML('beforeend', modalHtml);

        // Show modal
        const modal = new Modal(document.getElementById('deleteConfirmationModal'));
        modal.show();

        // Handle confirm button click
        document.getElementById('confirmDeleteBtn').addEventListener('click', () => {
            modal.hide();
            onConfirm();
        });

        // Clean up modal when hidden
        document.getElementById('deleteConfirmationModal').addEventListener('hidden.bs.modal', () => {
            document.getElementById('deleteConfirmationModal').remove();
        });
    }

    // Perform the actual delete operation
    performDelete(appointmentId) {
        const formData = new FormData();
        formData.append('action', 'delete_appointment');
        formData.append('appointment_id', appointmentId);
        formData.append('nonce', this.nonce);

        fetch(this.ajaxUrl, {
            method: 'POST',
            body: formData
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                const message = data.data.message || 'Appointment deleted successfully.';
                this.showNotification(message, 'success');
                
                // Redirect to appropriate dashboard based on user type from server
                setTimeout(() => {
                    const userType = data.data.user_type || 'member';
                    const homeUrl = data.data.home_url || window.location.origin;
                    const redirectUrl = userType === 'appraiser' ? homeUrl + '/appraiser-dashboard/' : homeUrl + '/member-dashboard/';
                    window.location.href = redirectUrl;
                }, 1500);
            } else {
                this.showNotification('Error: ' + data.data, 'danger');
            }
        })
        .catch(error => {
            console.error('Error:', error);
            this.showNotification('An error occurred. Please try again.', 'danger');
        });
    }

    // Book appointment
    bookAppointment(formData) {
        formData.append('action', 'book_appointment');
        formData.append('nonce', this.nonce);

        return fetch(this.ajaxUrl, {
            method: 'POST',
            body: formData
        })
        .then(response => response.json())
        .then(data => {
            if (data.success) {
                this.showNotification('Appointment request submitted successfully! The appraiser will review and approve your request.', 'success');
                return { success: true, data: data.data };
            } else {
                this.showNotification('Error: ' + data.data, 'danger');
                return { success: false, error: data.data };
            }
        })
        .catch(error => {
            console.error('Error:', error);
            this.showNotification('An error occurred. Please try again.', 'danger');
            return { success: false, error: error.message };
        });
    }

    // Show notification
    showNotification(message, type = 'info') {
        if (window.appraisalTheme && window.appraisalTheme.showNotification) {
            window.appraisalTheme.showNotification(message, type);
        } else if (window.appraisalTheme && window.appraisalTheme.notificationManager) {
            // Direct access to notification manager
            window.appraisalTheme.notificationManager.show(message, type);
        } else {
            // Fallback to alert if notification system not available
            alert(message);
        }
    }
}

// Export singleton instance
export const appointmentManager = new AppointmentManager();

